// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// ================================================================
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Eye, EyeOff, LogIn, UserPlus } from 'lucide-react';

export default function LoginPage() {
  const { signIn, signUp } = useAuth();
  const [mode, setMode]         = useState('login'); // login | signup
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [name, setName]         = useState('');
  const [showPw, setShowPw]     = useState(false);
  const [error, setError]       = useState('');
  const [success, setSuccess]   = useState('');
  const [loading, setLoading]   = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    setLoading(true);
    try {
      if (mode === 'login') {
        await signIn(email, password);
      } else {
        if (!name.trim()) { setError('Please enter your name'); setLoading(false); return; }
        await signUp(email, password, name);
        setSuccess('Account created! Check your email to confirm, then sign in.');
        setMode('login');
      }
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{
        background: '#fff', borderRadius: 20, padding: '40px 36px',
        width: '100%', maxWidth: 420,
        boxShadow: '0 8px 40px rgba(0,0,0,0.1)',
        border: '1px solid #BBF7D0',
      }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ fontSize: 52, marginBottom: 8 }}>🛒</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#14532D' }}>
            {mode === 'login' ? 'Welcome back!' : 'Create Account'}
          </div>
          <div style={{ fontSize: 14, color: '#64748B', marginTop: 4 }}>
            {mode === 'login' ? 'Sign in to shop, earn rewards, and track orders.' : 'Join to earn loyalty points and enjoy member deals.'}
          </div>
        </div>

        {error && (
          <div style={{ background: '#FEE2E2', color: '#DC2626', fontSize: 13, padding: '10px 14px', borderRadius: 10, marginBottom: 16, textAlign: 'center' }}>
            {error}
          </div>
        )}
        {success && (
          <div style={{ background: '#DCFCE7', color: '#16A34A', fontSize: 13, padding: '10px 14px', borderRadius: 10, marginBottom: 16, textAlign: 'center' }}>
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {mode === 'signup' && (
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#64748B', display: 'block', marginBottom: 6 }}>Full Name</label>
              <input className="input" type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Jane Smith" required />
            </div>
          )}
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 13, fontWeight: 600, color: '#64748B', display: 'block', marginBottom: 6 }}>Email</label>
            <input className="input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" required />
          </div>
          <div style={{ marginBottom: 22 }}>
            <label style={{ fontSize: 13, fontWeight: 600, color: '#64748B', display: 'block', marginBottom: 6 }}>Password</label>
            <div style={{ position: 'relative' }}>
              <input
                className="input"
                type={showPw ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder={mode === 'signup' ? 'Min 6 characters' : 'Enter password'}
                style={{ paddingRight: 44 }}
                required
              />
              <button type="button" onClick={() => setShowPw(!showPw)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', display: 'flex' }}>
                {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          <button type="submit" className="btn btn-primary btn-lg" style={{ width: '100%' }} disabled={loading}>
            {loading ? 'Please wait...' : mode === 'login' ? <><LogIn size={18} /> Sign In</> : <><UserPlus size={18} /> Create Account</>}
          </button>
        </form>

        {mode === 'login' && (
          <div style={{ textAlign: 'center', marginTop: 14 }}>
            <Link to="/forgot-password" style={{ fontSize: 13, color: '#16A34A', textDecoration: 'none', fontWeight: 500 }}>
              Forgot your password?
            </Link>
          </div>
        )}

        <div style={{ textAlign: 'center', marginTop: 12, fontSize: 14, color: '#64748B' }}>
          {mode === 'login' ? (
            <>
              New to Main Street Grocery?{' '}
              <span style={{ color: '#16A34A', fontWeight: 700, cursor: 'pointer' }} onClick={() => { setMode('signup'); setError(''); setSuccess(''); }}>
                Create account
              </span>
            </>
          ) : (
            <>
              Already have an account?{' '}
              <span style={{ color: '#16A34A', fontWeight: 700, cursor: 'pointer' }} onClick={() => { setMode('login'); setError(''); setSuccess(''); }}>
                Sign in
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
